/**
 * One toast call for the whole app, on ds-dlp's Toast (cascade plan 4.5).
 *
 * DLP's pages each kept their own snackbar state and drew an MUI <Snackbar>
 * with an <Alert>. They now call toast.success / error / warning / info, which
 * forwards to the one <ToastProvider> index.tsx mounts. It works outside React
 * too (api/client.ts). <ToastBridge /> inside the provider connects the two; a
 * call made before it mounts is held and shown once it does.
 *
 *   toast.success(message, { description?, duration?, id? })   and error / info / warning
 *   toast.loading(message)   stays open; returns an id
 *   toast.success(message, { id })   replaces the toast with that id (loading -> done)
 *   toast.dismiss(id?)
 *
 * With a description, the message is the title; without one it is the body.
 * New code in a component can call useToast() from ds-dlp directly for
 * actions and titles.
 *
 * Ported from DetectHub's lib/toast.ts (itself d2d-ZTA's), whose options,
 * loading and dismiss come with it.
 */
import { useEffect, type ReactNode } from 'react';
import { useToast, type ToastOptions, type ToastVariant } from '@/components/ds-dlp';

type Sink = { show: (opts: ToastOptions) => string; dismiss: (id: string) => void; dismissAll: () => void };

export interface ToastCallOptions {
  description?: ReactNode;
  /** Milliseconds; 0 or Infinity keeps it open. */
  duration?: number;
  /** Replace the toast with this id (as returned by an earlier call). */
  id?: string;
}

let sink: Sink | null = null;
const pending: { opts: ToastOptions; replace?: string }[] = [];

function show(opts: ToastOptions, replace?: string): string {
  if (!sink) {
    pending.push({ opts, replace });
    return '';
  }
  if (replace) sink.dismiss(replace);
  return sink.show(opts);
}

function call(variant: ToastVariant, message: ReactNode, o: ToastCallOptions = {}): string {
  const opts: ToastOptions = o.description
    ? { title: message, description: o.description, variant }
    : { description: message, variant };
  if (o.duration !== undefined) opts.duration = o.duration;
  return show(opts, o.id);
}

export const toast = Object.assign((message: ReactNode, o?: ToastCallOptions) => call('info', message, o), {
  success: (message: ReactNode, o?: ToastCallOptions) => call('success', message, o),
  error: (message: ReactNode, o?: ToastCallOptions) => call('danger', message, o),
  info: (message: ReactNode, o?: ToastCallOptions) => call('info', message, o),
  warning: (message: ReactNode, o?: ToastCallOptions) => call('warning', message, o),
  /** Stays open until replaced (pass its id to the next call) or dismissed. */
  loading: (message: ReactNode, o?: ToastCallOptions) => call('info', message, { duration: 0, ...o }),
  dismiss: (id?: string) => {
    if (!sink) return;
    if (id) sink.dismiss(id);
    else sink.dismissAll();
  },
});

export default toast;

/** Mount once, inside <ToastProvider>. Renders nothing. */
export function ToastBridge(): null {
  const { toast: showToast, dismiss, dismissAll } = useToast();
  useEffect(() => {
    const mine: Sink = { show: showToast, dismiss, dismissAll };
    sink = mine;
    for (const { opts, replace } of pending.splice(0)) {
      if (replace) dismiss(replace);
      showToast(opts);
    }
    return () => {
      if (sink === mine) sink = null;
    };
  }, [showToast, dismiss, dismissAll]);
  return null;
}
